import { AlertTriangleIcon, CheckCircleIcon, InfoIcon, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { EmptyValue } from '@/components/atoms/EmptyValue/EmptyValue';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { MarkdownText } from '@/components/atoms/MarkdownText/MarkdownText';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { SpinnerIcon } from '@/components/ui/spinner';
import { TabsContent } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import type { EvaluationWithSimulation } from '@/modules/simulations/types';
import { RESULT_STATUSES } from '@/modules/simulations/types';
import { timeFromNow } from '@/util/dateUtils';

type ResultVariant = 'pass' | 'fail' | 'running';

const RESULT_STYLES: Record<
  ResultVariant,
  { container: string; Icon?: LucideIcon; color: string; titleKey: string }
> = {
  pass: {
    container: 'border-successful bg-bg-success-soft',
    Icon: CheckCircleIcon,
    color: 'text-text-success-firm',
    titleKey: 'simulations.successful_result',
  },
  fail: {
    container: 'border-destructive bg-bg-destructive-soft',
    Icon: AlertTriangleIcon,
    color: 'text-destructive',
    titleKey: 'simulations.failed_result',
  },
  running: {
    container: 'border-informational bg-bg-info-soft',
    color: 'text-foreground',
    titleKey: 'simulations.in_progress',
  },
};

const getResultVariant = (status: EvaluationWithSimulation['status']): ResultVariant => {
  if (status === 'pass') {
    return 'pass';
  }
  if (status === 'fail' || status === 'error') {
    return 'fail';
  }
  return 'running';
};

interface ResultsTabProps {
  evaluation: EvaluationWithSimulation;
}

export const ResultsTab = ({ evaluation }: ResultsTabProps) => {
  const { t } = useTranslation();
  const { simulation } = evaluation;
  const variant = getResultVariant(evaluation.status);
  const {
    container: resultContainer,
    Icon: ResultIcon,
    color: resultColor,
    titleKey: resultTitleKey,
  } = RESULT_STYLES[variant];

  const isOutdated =
    RESULT_STATUSES.includes(evaluation.status) &&
    new Date(simulation.updated) > new Date(evaluation.updated);

  return (
    <TabsContent value="results" className="flex-1 space-y-4 overflow-y-auto p-4">
      <div className={cn('flex flex-col gap-1.5 rounded-md border p-4', resultContainer)}>
        <div className="flex items-center gap-1">
          {ResultIcon ? (
            <ResultIcon size={16} className={resultColor} />
          ) : (
            <SpinnerIcon className="size-4 text-accent" />
          )}
          <span className={cn('text-sm font-medium', resultColor)}>{t(resultTitleKey)}</span>
        </div>
        {variant !== 'running' &&
          (evaluation.evaluator_reasoning ? (
            <MarkdownText
              markdown={evaluation.evaluator_reasoning}
              className="text-sm text-foreground"
            />
          ) : (
            <EmptyValue />
          ))}
      </div>

      <div>
        <span className="text-xs text-foreground-muted">{t('simulations.simulation_date')}</span>
        <p className="mt-0.5 text-sm text-foreground">
          <TimestampTooltip timestamp={evaluation.created}>
            {timeFromNow(evaluation.created)}
          </TimestampTooltip>
          {isOutdated && (
            <span className="ml-1">
              {t('simulations.outdated')}
              <InfoTooltip content={t('simulations.outdated_tooltip')} size="large" side="top">
                <InfoIcon className="ml-1 inline size-3.5 align-middle text-foreground-muted" />
              </InfoTooltip>
            </span>
          )}
        </p>
      </div>

      <div>
        <span className="text-xs text-foreground-muted">{t('simulations.scenario')}</span>
        <MarkdownText markdown={simulation.scenario} className="mt-1 text-sm text-foreground" />
      </div>

      <div>
        <span className="text-xs text-foreground-muted">{t('simulations.pass_condition')}</span>
        <MarkdownText
          markdown={simulation.pass_criteria}
          className="mt-1 text-sm text-foreground"
        />
      </div>
    </TabsContent>
  );
};
